import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';

interface Template {
  id: number;
  name: string;
  name_ar?: string;
  description?: string;
  description_ar?: string;
  version: number;
  active: boolean;
}

interface TemplateStep {
  id?: number;
  step_number: number;
  title: string;
  title_ar?: string;
  description?: string;
  description_ar?: string;
  sort_order: number;
  fields?: TemplateField[];
}

interface TemplateField {
  id?: number;
  field_key: string;
  field_type: 'text' | 'textarea' | 'number' | 'email' | 'phone' | 'url' | 'date' | 'dropdown' | 'checkbox' | 'radio' | 'file';
  label: string;
  label_ar?: string;
  placeholder?: string;
  placeholder_ar?: string;
  is_required: boolean;
  validation_rules?: any;
  sort_order: number;
  active: boolean;
  options?: FieldOption[];
}

interface FieldOption {
  id?: number;
  option_value: string;
  option_label: string;
  option_label_ar?: string;
  sort_order: number;
  active: boolean;
}

const TemplateBuilder: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [template, setTemplate] = useState<Template | null>(null);
  const [steps, setSteps] = useState<TemplateStep[]>([]);
  const [activeStep, setActiveStep] = useState<number | null>(null);
  const [showStepModal, setShowStepModal] = useState(false);
  const [showFieldModal, setShowFieldModal] = useState(false);
  const [editingStep, setEditingStep] = useState<TemplateStep | null>(null);
  const [editingField, setEditingField] = useState<TemplateField | null>(null);
  const [activeTab, setActiveTab] = useState<'english' | 'arabic'>('english');
  const [bulkOptions, setBulkOptions] = useState('');

  // Mandatory field validation
  const MANDATORY_FIELDS = [
    { key: 'company_name', label: 'Company/Business Name' },
    { key: 'business_type', label: 'Business Type/Industry' }
  ];

  const getMandatoryFieldValidation = () => {
    const allFieldKeys = steps.flatMap(step =>
      step.fields?.map(field => field.field_key) || []
    );

    const missingFields = MANDATORY_FIELDS.filter(
      mandatoryField => !allFieldKeys.includes(mandatoryField.key)
    );

    const hasAllMandatory = missingFields.length === 0;

    return {
      isValid: hasAllMandatory,
      missingFields,
      message: hasAllMandatory
        ? 'All mandatory fields are present'
        : `Missing mandatory fields: ${missingFields.map(f => f.label).join(', ')}`
    };
  };

  const getFieldKeyValidation = (fieldKey: string, excludeFieldId?: number) => {
    if (!fieldKey.trim()) {
      return {
        isValid: false,
        message: 'Field key is required',
        type: 'required'
      };
    }

    // Get all existing field keys across all steps, excluding the current field being edited
    const allFieldKeys = steps.flatMap(step =>
      step.fields?.filter(field => field.id !== excludeFieldId)
        .map(field => field.field_key) || []
    );

    const isDuplicate = allFieldKeys.includes(fieldKey);

    if (isDuplicate) {
      // Find which step contains the duplicate
      const duplicateStep = steps.find(step =>
        step.fields?.some(field =>
          field.field_key === fieldKey && field.id !== excludeFieldId
        )
      );

      return {
        isValid: false,
        message: `Field key '${fieldKey}' already exists in "${duplicateStep?.title || 'another step'}"`,
        type: 'duplicate',
        conflictStep: duplicateStep
      };
    }

    return {
      isValid: true,
      message: 'Field key is unique',
      type: 'valid'
    };
  };

  // Step form data
  const [stepFormData, setStepFormData] = useState({
    step_number: 1,
    title: '',
    title_ar: '',
    description: '',
    description_ar: '',
    sort_order: 0
  });

  // Field form data
  const [fieldFormData, setFieldFormData] = useState<TemplateField>({
    field_key: '',
    field_type: 'text',
    label: '',
    label_ar: '',
    placeholder: '',
    placeholder_ar: '',
    is_required: false,
    validation_rules: {},
    sort_order: 0,
    active: true,
    options: []
  });

  useEffect(() => {
    if (id) {
      fetchTemplate();
      fetchSteps();
    }
  }, [id]);

  // Add useEffect to ensure loading is handled properly - wait for template and steps with fields
  useEffect(() => {
    if (template && steps.length > 0) {
      // Check if all steps have their fields loaded
      const allStepsHaveFields = steps.every(step =>
        step.fields !== undefined // fields property exists (even if empty array)
      );

      if (allStepsHaveFields) {
        setLoading(false);      }
    } else if (template && steps.length === 0) {
      // Template exists but has no steps, so we're fully loaded
      setLoading(false);    }
  }, [template, steps]);

  const fetchTemplate = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`/backend/api/admin/templates/?id=${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();
      if (data && (data.id || data.name)) {
        // Direct template object response
        setTemplate(data);
      } else if (data.template) {
        setTemplate(data.template);
      } else if (data.success && data.template) {
        // Fallback for success wrapper format
        setTemplate(data.template);
      } else {      }
    } catch (error) {    }
  };

  const fetchSteps = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      if (!token) {        setSteps([]);
        return;
      }

      const response = await fetch(`/backend/api/admin/templates/steps.php?template_id=${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();
      let stepsData = [];
      if (data.steps) {
        stepsData = data.steps;
      } else if (data.success && data.steps) {
        stepsData = data.steps;
      } else {        setSteps([]);
        return;
      }

      // Set steps first
      setSteps(stepsData);      // Then fetch fields for all steps automatically
      if (stepsData.length > 0) {
        await fetchAllStepFields(stepsData, token);
      }
    } catch (error) {      setSteps([]);
    }
  };

  // New function to fetch fields for all steps
  const fetchAllStepFields = async (stepsData: TemplateStep[], token: string) => {
    try {
      // Fetch fields for all steps in parallel
      const fieldPromises = stepsData.map(async (step) => {
        try {
          const response = await fetch(`/backend/api/admin/templates/fields.php?step_id=${step.id}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });
          const data = await response.json();
          return {
            stepId: step.id,
            fields: data.fields || (data.success ? data.fields : [])
          };
        } catch (error) {          return {
            stepId: step.id,
            fields: []
          };
        }
      });

      // Wait for all field requests to complete
      const fieldResults = await Promise.all(fieldPromises);

      // Update all steps with their fields at once
      setSteps(prev => prev.map(step => {
        const fieldResult = fieldResults.find(result => result.stepId === step.id);
        return fieldResult ? { ...step, fields: fieldResult.fields } : step;
      }));    } catch (error) {    }
  };

  const fetchFields = async (stepId: number) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`/backend/api/admin/templates/fields.php?step_id=${stepId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();
      if (data.fields) {
        setSteps(prev => prev.map(step =>
          step.id === stepId ? { ...step, fields: data.fields } : step
        ));
      } else if (data.success && data.fields) {
        // Fallback for success wrapper format
        setSteps(prev => prev.map(step =>
          step.id === stepId ? { ...step, fields: data.fields } : step
        ));
      }
    } catch (error) {    }
  };

  const openStepModal = (step: TemplateStep | null = null) => {
    if (step) {
      setEditingStep(step);
      setStepFormData({
        step_number: step.step_number,
        title: step.title,
        title_ar: step.title_ar || '',
        description: step.description || '',
        description_ar: step.description_ar || '',
        sort_order: step.sort_order
      });
    } else {
      setEditingStep(null);
      setStepFormData({
        step_number: steps.length + 1,
        title: '',
        title_ar: '',
        description: '',
        description_ar: '',
        sort_order: steps.length
      });
    }
    setActiveTab('english');
    setShowStepModal(true);
  };

  const openFieldModal = (field: TemplateField | null = null) => {
    if (field) {
      setEditingField(field);
      setFieldFormData(field);
    } else {
      setEditingField(null);
      const stepFields = steps.find(s => s.id === activeStep)?.fields || [];
      setFieldFormData({
        field_key: '',
        field_type: 'text',
        label: '',
        label_ar: '',
        placeholder: '',
        placeholder_ar: '',
        is_required: false,
        validation_rules: {},
        sort_order: stepFields.length,
        active: true,
        options: []
      });
    }
    setActiveTab('english');
    setBulkOptions(''); // Clear bulk options input
    setShowFieldModal(true);
  };

  const handleStepInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setStepFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFieldInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFieldFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleStepSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const token = localStorage.getItem('admin_token');
      const method = editingStep ? 'PUT' : 'POST';
      const url = editingStep
        ? `/backend/api/admin/templates/steps.php?id=${editingStep.id}`
        : '/backend/api/admin/templates/steps.php';

      const submitData = {
        ...stepFormData,
        template_id: parseInt(id!)
      };

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submitData),
      });

      const data = await response.json();

      if (data.success) {
        // Reset form data
        setStepFormData({
          step_number: steps.length + 1,
          title: '',
          title_ar: '',
          description: '',
          description_ar: '',
          sort_order: steps.length
        });

        // Refresh steps and close modal
        await fetchSteps();
        setShowStepModal(false);
        setEditingStep(null);
        setActiveTab('english');
        alert(editingStep ? 'Step updated successfully!' : 'Step created successfully!');
      } else {
        alert('Error: ' + (data.error || 'Failed to save step'));
      }
    } catch (error) {      alert('Network error occurred');
    }
  };

  const handleFieldSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate field key uniqueness before submitting
    const fieldKeyValidation = getFieldKeyValidation(fieldFormData.field_key, editingField?.id);
    if (!fieldKeyValidation.isValid) {
      alert(`Validation Error: ${fieldKeyValidation.message}`);
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      const method = editingField ? 'PUT' : 'POST';
      const url = editingField
        ? `/backend/api/admin/templates/fields.php?id=${editingField.id}`
        : '/backend/api/admin/templates/fields.php';

      // Prepare clean data for backend
      const cleanValidationRules = fieldFormData.validation_rules && Object.keys(fieldFormData.validation_rules).length > 0
        ? Object.fromEntries(
            Object.entries(fieldFormData.validation_rules).filter(([key, value]) =>
              value !== '' && value !== null && value !== undefined
            )
          )
        : {};

      // Simplify options structure for backend compatibility
      const cleanOptions: Array<{
        value: string;
        label: string;
        label_ar: string;
        sort_order: number;
        active: boolean;
      }> = fieldFormData.options && fieldFormData.options.length > 0
        ? fieldFormData.options.map((option, index) => ({
            value: option.option_value,
            label: option.option_label,
            label_ar: option.option_label_ar || option.option_label,
            sort_order: index,
            active: true
          }))
        : [];

      const submitData = {
        field_key: fieldFormData.field_key,
        field_type: fieldFormData.field_type,
        label: fieldFormData.label,
        label_ar: fieldFormData.label_ar || '',
        placeholder: fieldFormData.placeholder || '',
        placeholder_ar: fieldFormData.placeholder_ar || '',
        is_required: fieldFormData.is_required ? 1 : 0,
        sort_order: fieldFormData.sort_order || 0,
        active: fieldFormData.active ? 1 : 0,
        step_id: activeStep,
        validation_rules: Object.keys(cleanValidationRules).length > 0 ? JSON.stringify(cleanValidationRules) : '{}',
        options: cleanOptions
      };

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submitData),
      });

      const data = await response.json();

      if (data.success) {
        if (activeStep) {
          await fetchFields(activeStep);
        }
        setShowFieldModal(false);
        setEditingField(null);
        alert(editingField ? 'Field updated successfully!' : 'Field created successfully!');
      } else {
        alert('Error: ' + (data.error || 'Failed to save field'));
      }
    } catch (error) {      alert('Network error occurred');
    }
  };

  const handleDeleteStep = async (stepId: number) => {
    if (!window.confirm('Are you sure you want to delete this step? This will also delete all fields in this step.')) return;

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`/backend/api/admin/templates/steps.php?id=${stepId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (data.success) {
        await fetchSteps();
        if (activeStep === stepId) {
          setActiveStep(null);
        }
        alert('Step deleted successfully!');
      } else {
        alert('Error: ' + (data.error || 'Failed to delete step'));
      }
    } catch (error) {      alert('Network error occurred');
    }
  };

  const handleDeleteField = async (fieldId: number) => {
    if (!window.confirm('Are you sure you want to delete this field?')) return;

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`/backend/api/admin/templates/fields.php?id=${fieldId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (data.success) {
        if (activeStep) {
          await fetchFields(activeStep);
        }
        alert('Field deleted successfully!');
      } else {
        alert('Error: ' + (data.error || 'Failed to delete field'));
      }
    } catch (error) {      alert('Network error occurred');
    }
  };

  const selectStep = (stepId: number) => {
    setActiveStep(stepId);
    fetchFields(stepId);
  };

  const getFieldTypeIcon = (type: string) => {
    const icons: Record<string, string> = {
      'text': '📝',
      'textarea': '📄',
      'number': '🔢',
      'email': '📧',
      'phone': '📞',
      'url': '🔗',
      'date': '📅',
      'dropdown': '📋',
      'checkbox': '☑️',
      'radio': '🔘',
      'file': '📁'
    };
    return icons[type] || '📝';
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout fullWidth>
      <div className="h-screen flex flex-col bg-gray-50" dir="ltr">
        {/* Header */}
        <div className="bg-white shadow-sm border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/admin/packages?tab=templates')}
                className="text-gray-600 hover:text-gray-900"
              >
                ← Back to Templates
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">
                  Template Builder: {template?.name}
                </h1>
                {template?.name_ar && (
                  <p className="text-sm text-gray-600" dir="rtl">{template.name_ar}</p>
                )}
              </div>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => openStepModal()}
                className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors"
              >
                Add Step
              </button>
            </div>
          </div>

          {/* Template Validation Banner */}
          {(() => {
            const mandatoryValidation = getMandatoryFieldValidation();

            // Check for duplicate field keys
            const allFieldKeys = steps.flatMap(step =>
              step.fields?.map(field => field.field_key) || []
            );
            const duplicateKeys = allFieldKeys.filter((key, index) =>
              allFieldKeys.indexOf(key) !== index
            );
            const hasNoDuplicates = duplicateKeys.length === 0;

            const isFullyValid = mandatoryValidation.isValid && hasNoDuplicates;

            return (
              <div className={`mt-4 p-3 rounded-lg ${
                isFullyValid
                  ? 'bg-green-50 border border-green-200'
                  : 'bg-red-50 border border-red-200'
              }`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <span className={`text-lg ${isFullyValid ? 'text-green-600' : 'text-red-600'}`}>
                      {isFullyValid ? '✅' : '⚠️'}
                    </span>
                    <div>
                      <p className={`font-medium text-sm ${isFullyValid ? 'text-green-800' : 'text-red-800'}`}>
                        {isFullyValid ? 'Template Validation Passed' : 'Template Validation Failed'}
                      </p>
                      <div className={`text-xs ${isFullyValid ? 'text-green-600' : 'text-red-600'} space-y-1`}>
                        <p>Mandatory fields: {mandatoryValidation.isValid ? '✓ Complete' : '❌ Missing'}</p>
                        <p>Field uniqueness: {hasNoDuplicates ? '✓ All unique' : '❌ Duplicates found'}</p>
                      </div>
                    </div>
                  </div>

                  <div className="flex space-x-4">
                    {/* Missing Mandatory Fields */}
                    {!mandatoryValidation.isValid && (
                      <div className="text-xs text-red-600">
                        <p className="font-medium">Missing Required Fields:</p>
                        <ul className="list-disc list-inside">
                          {mandatoryValidation.missingFields.map(field => (
                            <li key={field.key}>
                              <code className="bg-red-100 px-1 py-0.5 rounded text-xs">{field.key}</code>
                              - {field.label}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Duplicate Field Keys */}
                    {!hasNoDuplicates && (
                      <div className="text-xs text-red-600">
                        <p className="font-medium">Duplicate Field Keys:</p>
                        <ul className="list-disc list-inside">
                          {Array.from(new Set(duplicateKeys)).map(key => (
                            <li key={key}>
                              <code className="bg-red-100 px-1 py-0.5 rounded text-xs">{key}</code>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })()}
        </div>

        {/* Main Content */}
        <div className="flex flex-1 overflow-hidden">
          {/* Steps Sidebar */}
          <div className="w-80 bg-white shadow-sm border-r border-gray-200 overflow-y-auto">
            <div className="p-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Template Steps</h3>
              <p className="text-sm text-gray-600">Click on a step to edit its fields</p>
            </div>

            <div className="space-y-2 p-4">
              {steps.map((step, index) => (
                <div
                  key={step.id}
                  className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                    activeStep === step.id
                      ? 'bg-primary-50 border-primary-200'
                      : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                  }`}
                  onClick={() => selectStep(step.id!)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <span className="text-xs font-medium text-gray-500">
                          Step {step.step_number}
                        </span>
                      </div>
                      <h4 className="font-medium text-gray-900 truncate">{step.title}</h4>
                      {step.title_ar && (
                        <p className="text-sm text-gray-600 truncate" dir="rtl">{step.title_ar}</p>
                      )}
                      <p className="text-xs text-gray-500 mt-1">
                        {step.fields?.length || 0} fields
                      </p>
                    </div>
                    <div className="flex flex-col space-y-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openStepModal(step);
                        }}
                        className="text-xs text-blue-600 hover:text-blue-800"
                      >
                        Edit
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteStep(step.id!);
                        }}
                        className="text-xs text-red-600 hover:text-red-800"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}

              {steps.length === 0 && (
                <div className="text-center py-8">
                  <p className="text-gray-500">No steps created yet</p>
                  <button
                    onClick={() => openStepModal()}
                    className="text-primary-600 hover:text-primary-800 text-sm font-medium mt-2"
                  >
                    Create first step
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Fields Panel */}
          <div className="flex-1 overflow-y-auto">
            {activeStep ? (
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">
                      Step Fields: {steps.find(s => s.id === activeStep)?.title}
                    </h3>
                    <p className="text-sm text-gray-600">Configure the fields for this step</p>
                  </div>
                  <button
                    onClick={() => openFieldModal()}
                    className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
                  >
                    Add Field
                  </button>
                </div>

                <div className="space-y-4">
                  {steps.find(s => s.id === activeStep)?.fields?.map((field, index) => {
                    const isMandatory = MANDATORY_FIELDS.some(m => m.key === field.field_key);

                    return (
                      <div
                        key={field.id}
                        className={`bg-white p-4 rounded-lg border ${
                          isMandatory
                            ? 'border-green-300 bg-green-50 shadow-sm'
                            : 'border-gray-200'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <span className="text-2xl">{getFieldTypeIcon(field.field_type)}</span>
                            <div>
                              <div className="flex items-center space-x-2">
                                <h4 className="font-medium text-gray-900">{field.label}</h4>
                                {isMandatory && (
                                  <span className="text-xs font-medium text-green-700 bg-green-100 px-2 py-1 rounded-full flex items-center space-x-1">
                                    <span>✓</span>
                                    <span>System Required</span>
                                  </span>
                                )}
                              </div>
                              {field.label_ar && (
                                <p className="text-sm text-gray-600" dir="rtl">{field.label_ar}</p>
                              )}
                              <div className="flex items-center space-x-4 mt-1">
                                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                                  {field.field_type}
                                </span>
                                <span className={`text-xs px-2 py-1 rounded font-mono ${
                                  isMandatory
                                    ? 'text-green-700 bg-green-100 font-semibold'
                                    : 'text-gray-500 bg-gray-100'
                                }`}>
                                  Key: {field.field_key}
                                </span>
                                {field.is_required && (
                                  <span className="text-xs text-red-500 bg-red-50 px-2 py-1 rounded">
                                    Required
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => openFieldModal(field)}
                            className="text-blue-600 hover:text-blue-800 text-sm"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteField(field.id!)}
                            className="text-red-600 hover:text-red-800 text-sm"
                          >
                            Delete
                          </button>
                        </div>
                      </div>

                      {field.options && field.options.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-gray-100">
                          <p className="text-xs text-gray-500 mb-2">Options:</p>
                          <div className="flex flex-wrap gap-2">
                            {field.options.map((option, optIndex) => (
                              <span key={optIndex} className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded">
                                {option.option_label}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    );
                  }) || (
                    <div className="text-center py-12">
                      <p className="text-gray-500">No fields in this step</p>
                      <button
                        onClick={() => openFieldModal()}
                        className="text-primary-600 hover:text-primary-800 text-sm font-medium mt-2"
                      >
                        Add first field
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <p className="text-gray-500 text-lg">Select a step to manage its fields</p>
                  <p className="text-gray-400 text-sm mt-2">Choose a step from the left panel to get started</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Step Modal */}
        {showStepModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50" dir="ltr">
            <div className="relative top-20 mx-auto p-6 border w-full max-w-2xl shadow-lg rounded-lg bg-white">
              <h3 className="text-xl font-semibold text-gray-900 mb-6">
                {editingStep ? 'Edit Step' : 'Create New Step'}
              </h3>

              {/* Tab Navigation */}
              <div className="border-b border-gray-200 mb-6">
                <nav className="-mb-px flex space-x-8">
                  <button
                    type="button"
                    onClick={() => setActiveTab('english')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                      activeTab === 'english'
                        ? 'border-primary-500 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    English Content
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab('arabic')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                      activeTab === 'arabic'
                        ? 'border-primary-500 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Arabic Content (العربية)
                  </button>
                </nav>
              </div>

              <form onSubmit={handleStepSubmit} className="space-y-6">
                {/* Step Number */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Step Number *
                  </label>
                  <input
                    type="number"
                    name="step_number"
                    value={stepFormData.step_number}
                    onChange={handleStepInputChange}
                    min="1"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                {/* Tabbed Content */}
                <div className="min-h-[300px]">
                  {activeTab === 'english' && (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Step Title *
                        </label>
                        <input
                          type="text"
                          name="title"
                          value={stepFormData.title}
                          onChange={handleStepInputChange}
                          required
                          placeholder="Enter step title in English"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Description
                        </label>
                        <textarea
                          name="description"
                          value={stepFormData.description}
                          onChange={handleStepInputChange}
                          rows={4}
                          placeholder="Enter step description in English"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>
                    </div>
                  )}

                  {activeTab === 'arabic' && (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          عنوان الخطوة (Step Title)
                        </label>
                        <input
                          type="text"
                          name="title_ar"
                          value={stepFormData.title_ar}
                          onChange={handleStepInputChange}
                          dir="rtl"
                          placeholder="أدخل عنوان الخطوة بالعربية"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          الوصف (Description)
                        </label>
                        <textarea
                          name="description_ar"
                          value={stepFormData.description_ar}
                          onChange={handleStepInputChange}
                          rows={4}
                          dir="rtl"
                          placeholder="أدخل وصف الخطوة بالعربية"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Form Actions */}
                <div className="border-t pt-6 bg-gray-50 -mx-6 -mb-6 px-6 pb-6 rounded-b-lg">
                  <div className="flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={() => setShowStepModal(false)}
                      className="px-6 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-6 py-2 bg-primary-600 text-white rounded-md text-sm font-medium hover:bg-primary-700 transition-colors"
                    >
                      {editingStep ? 'Update' : 'Create'} Step
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Field Modal */}
        {showFieldModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50" dir="ltr">
            <div className="relative top-10 mx-auto p-6 border w-full max-w-4xl shadow-lg rounded-lg bg-white">
              <h3 className="text-xl font-semibold text-gray-900 mb-6">
                {editingField ? 'Edit Field' : 'Create New Field'}
              </h3>

              {/* Tab Navigation */}
              <div className="border-b border-gray-200 mb-6">
                <nav className="-mb-px flex space-x-8">
                  <button
                    type="button"
                    onClick={() => setActiveTab('english')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                      activeTab === 'english'
                        ? 'border-primary-500 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    English Content
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab('arabic')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                      activeTab === 'arabic'
                        ? 'border-primary-500 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Arabic Content (العربية)
                  </button>
                </nav>
              </div>

              <form onSubmit={handleFieldSubmit} className="space-y-6">
                {/* Basic Field Information */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-sm font-semibold text-gray-700 mb-4">Basic Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Field Key * (unique identifier)
                      </label>
                      {(() => {
                        const validation = getFieldKeyValidation(fieldFormData.field_key, editingField?.id);
                        const isMandatory = MANDATORY_FIELDS.some(m => m.key === fieldFormData.field_key);

                        return (
                          <>
                            <input
                              type="text"
                              name="field_key"
                              value={fieldFormData.field_key}
                              onChange={handleFieldInputChange}
                              required
                              placeholder="e.g., company_name"
                              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 ${
                                !fieldFormData.field_key.trim()
                                  ? 'border-gray-300'
                                  : !validation.isValid
                                  ? 'border-red-300 bg-red-50'
                                  : isMandatory
                                  ? 'border-green-300 bg-green-50'
                                  : 'border-blue-300 bg-blue-50'
                              }`}
                            />

                            {/* Real-time Validation Feedback */}
                            {fieldFormData.field_key.trim() && (
                              <div className={`mt-1 p-2 rounded-md text-xs ${
                                !validation.isValid
                                  ? 'bg-red-50 border border-red-200 text-red-700'
                                  : isMandatory
                                  ? 'bg-green-50 border border-green-200 text-green-700'
                                  : 'bg-blue-50 border border-blue-200 text-blue-700'
                              }`}>
                                <div className="flex items-center space-x-1">
                                  <span>
                                    {!validation.isValid ? '❌' : isMandatory ? '✅' : '✓'}
                                  </span>
                                  <span className="font-medium">{validation.message}</span>
                                </div>
                                {validation.type === 'duplicate' && validation.conflictStep && (
                                  <div className="mt-1 text-xs">
                                    Conflict in step: "{validation.conflictStep.title}"
                                  </div>
                                )}
                              </div>
                            )}
                          </>
                        );
                      })()}

                      {/* Mandatory Fields Helper */}
                      <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-md">
                        <p className="text-xs font-medium text-blue-800 mb-1">Required Template Fields:</p>
                        <div className="space-y-1">
                          {MANDATORY_FIELDS.map(field => {
                            const isPresent = steps.some(step =>
                              step.fields?.some(f => f.field_key === field.key)
                            );
                            const isCurrent = fieldFormData.field_key === field.key;

                            return (
                              <div key={field.key} className="flex items-center justify-between">
                                <button
                                  type="button"
                                  onClick={() => setFieldFormData({...fieldFormData, field_key: field.key})}
                                  className="text-xs text-blue-700 hover:text-blue-900 font-mono bg-blue-100 px-2 py-1 rounded hover:bg-blue-200 transition-colors"
                                >
                                  {field.key}
                                </button>
                                <div className="flex items-center space-x-1">
                                  <span className="text-xs text-gray-600">{field.label}</span>
                                  {isPresent && !isCurrent && (
                                    <span className="text-xs text-green-600">✓</span>
                                  )}
                                  {isCurrent && (
                                    <span className="text-xs text-blue-600">Current</span>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Show All Existing Field Keys for Reference */}
                      {(() => {
                        const allFieldKeys = steps.flatMap(step =>
                          step.fields?.filter(f => f.id !== editingField?.id)
                            .map(f => ({ key: f.field_key, step: step.title })) || []
                        );

                        if (allFieldKeys.length > 0) {
                          return (
                            <div className="mt-2 p-2 bg-gray-50 border border-gray-200 rounded-md">
                              <p className="text-xs font-medium text-gray-700 mb-1">Existing Fields in Template:</p>
                              <div className="flex flex-wrap gap-1">
                                {allFieldKeys.map((field, index) => (
                                  <span
                                    key={index}
                                    className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded font-mono"
                                    title={`In step: ${field.step}`}
                                  >
                                    {field.key}
                                  </span>
                                ))}
                              </div>
                            </div>
                          );
                        }
                        return null;
                      })()}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Field Type *
                      </label>
                      <select
                        name="field_type"
                        value={fieldFormData.field_type}
                        onChange={handleFieldInputChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                      >
                        <option value="text">Text</option>
                        <option value="textarea">Textarea</option>
                        <option value="number">Number</option>
                        <option value="email">Email</option>
                        <option value="phone">Phone</option>
                        <option value="url">URL</option>
                        <option value="date">Date</option>
                        <option value="dropdown">Dropdown</option>
                        <option value="checkbox">Checkbox</option>
                        <option value="radio">Radio</option>
                        <option value="file">File</option>
                      </select>
                    </div>
                    <div className="flex items-end">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          name="is_required"
                          checked={fieldFormData.is_required}
                          onChange={handleFieldInputChange}
                          className="mr-2"
                        />
                        <span className="text-sm text-gray-700">Required Field</span>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Tabbed Content */}
                <div className="min-h-[300px]">
                  {activeTab === 'english' && (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Field Label *
                        </label>
                        <input
                          type="text"
                          name="label"
                          value={fieldFormData.label}
                          onChange={handleFieldInputChange}
                          required
                          placeholder="Enter field label in English"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Placeholder Text
                        </label>
                        <input
                          type="text"
                          name="placeholder"
                          value={fieldFormData.placeholder || ''}
                          onChange={handleFieldInputChange}
                          placeholder="Enter placeholder text in English"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>
                    </div>
                  )}

                  {activeTab === 'arabic' && (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          تسمية الحقل (Field Label)
                        </label>
                        <input
                          type="text"
                          name="label_ar"
                          value={fieldFormData.label_ar || ''}
                          onChange={handleFieldInputChange}
                          dir="rtl"
                          placeholder="أدخل تسمية الحقل بالعربية"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          النص التوضيحي (Placeholder)
                        </label>
                        <input
                          type="text"
                          name="placeholder_ar"
                          value={fieldFormData.placeholder_ar || ''}
                          onChange={handleFieldInputChange}
                          dir="rtl"
                          placeholder="أدخل النص التوضيحي بالعربية"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Field Type Specific Configurations */}
                {(fieldFormData.field_type === 'dropdown' || fieldFormData.field_type === 'radio') && (
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h4 className="text-sm font-semibold text-gray-700 mb-4">Options Configuration</h4>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Bulk Options Input (One option per line)
                        </label>
                        <textarea
                          value={bulkOptions}
                          onChange={(e) => setBulkOptions(e.target.value)}
                          rows={5}
                          placeholder="Option 1&#10;Option 2&#10;Option 3&#10;..."
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 font-mono text-sm"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Paste your options here, one per line. They will be displayed in the same order.
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          const lines = bulkOptions.split('\n').filter(line => line.trim());
                          const newOptions = lines.map((line, index) => ({
                            option_value: line.trim().toLowerCase().replace(/\s+/g, '_'),
                            option_label: line.trim(),
                            option_label_ar: line.trim(), // Keep same as English for now, admin can edit
                            sort_order: index,
                            active: true
                          }));
                          // Preserve existing options and add new ones
                          setFieldFormData(prev => ({
                            ...prev,
                            options: [...(prev.options || []), ...newOptions]
                          }));
                          setBulkOptions('');
                        }}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 transition-colors"
                      >
                        Add Options from Text
                      </button>

                      {/* Display Current Options */}
                      {fieldFormData.options && fieldFormData.options.length > 0 && (
                        <div className="mt-4">
                          <h5 className="text-sm font-medium text-gray-700 mb-2">Current Options:</h5>
                          <div className="space-y-2 max-h-40 overflow-y-auto">
                            {fieldFormData.options.map((option, index) => (
                              <div key={index} className="bg-white p-3 rounded border">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
                                  <input
                                    type="text"
                                    value={option.option_label}
                                    onChange={(e) => {
                                      const newOptions = [...fieldFormData.options!];
                                      newOptions[index] = { ...newOptions[index], option_label: e.target.value };
                                      setFieldFormData(prev => ({ ...prev, options: newOptions }));
                                    }}
                                    placeholder="English option"
                                    className="text-sm px-2 py-1 border rounded"
                                  />
                                  <input
                                    type="text"
                                    value={option.option_label_ar || ''}
                                    onChange={(e) => {
                                      const newOptions = [...fieldFormData.options!];
                                      newOptions[index] = { ...newOptions[index], option_label_ar: e.target.value };
                                      setFieldFormData(prev => ({ ...prev, options: newOptions }));
                                    }}
                                    placeholder="Arabic option"
                                    dir="rtl"
                                    className="text-sm px-2 py-1 border rounded"
                                  />
                                </div>
                                <div className="flex justify-between items-center">
                                  <span className="text-xs text-gray-500">Value: {option.option_value}</span>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const newOptions = fieldFormData.options!.filter((_, i) => i !== index);
                                      setFieldFormData(prev => ({ ...prev, options: newOptions }));
                                    }}
                                    className="text-red-500 hover:text-red-700 text-xs px-2 py-1 rounded"
                                  >
                                    Remove
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Number Field Configuration */}
                {fieldFormData.field_type === 'number' && (
                  <div className="bg-green-50 p-4 rounded-lg">
                    <h4 className="text-sm font-semibold text-gray-700 mb-4">Number Field Configuration</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Minimum Value</label>
                        <input
                          type="number"
                          value={fieldFormData.validation_rules?.min || ''}
                          onChange={(e) => setFieldFormData(prev => ({
                            ...prev,
                            validation_rules: { ...prev.validation_rules, min: e.target.value }
                          }))}
                          placeholder="e.g., 0"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Maximum Value</label>
                        <input
                          type="number"
                          value={fieldFormData.validation_rules?.max || ''}
                          onChange={(e) => setFieldFormData(prev => ({
                            ...prev,
                            validation_rules: { ...prev.validation_rules, max: e.target.value }
                          }))}
                          placeholder="e.g., 100"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Step Increment</label>
                        <input
                          type="number"
                          value={fieldFormData.validation_rules?.step || ''}
                          onChange={(e) => setFieldFormData(prev => ({
                            ...prev,
                            validation_rules: { ...prev.validation_rules, step: e.target.value }
                          }))}
                          placeholder="e.g., 1"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Text Field Configuration */}
                {(fieldFormData.field_type === 'text' || fieldFormData.field_type === 'textarea') && (
                  <div className="bg-yellow-50 p-4 rounded-lg">
                    <h4 className="text-sm font-semibold text-gray-700 mb-4">Text Field Configuration</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Minimum Length</label>
                        <input
                          type="number"
                          value={fieldFormData.validation_rules?.minLength || ''}
                          onChange={(e) => setFieldFormData(prev => ({
                            ...prev,
                            validation_rules: { ...prev.validation_rules, minLength: e.target.value }
                          }))}
                          placeholder="e.g., 3"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Maximum Length</label>
                        <input
                          type="number"
                          value={fieldFormData.validation_rules?.maxLength || ''}
                          onChange={(e) => setFieldFormData(prev => ({
                            ...prev,
                            validation_rules: { ...prev.validation_rules, maxLength: e.target.value }
                          }))}
                          placeholder="e.g., 50"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* File Field Configuration */}
                {fieldFormData.field_type === 'file' && (
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <h4 className="text-sm font-semibold text-gray-700 mb-4">File Upload Configuration</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Allowed File Types</label>
                        <input
                          type="text"
                          value={fieldFormData.validation_rules?.allowedTypes || ''}
                          onChange={(e) => setFieldFormData(prev => ({
                            ...prev,
                            validation_rules: { ...prev.validation_rules, allowedTypes: e.target.value }
                          }))}
                          placeholder="e.g., pdf,doc,docx,jpg,png"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                        />
                        <p className="text-xs text-gray-500 mt-1">Comma-separated file extensions</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Max File Size (MB)</label>
                        <input
                          type="number"
                          value={fieldFormData.validation_rules?.maxSize || ''}
                          onChange={(e) => setFieldFormData(prev => ({
                            ...prev,
                            validation_rules: { ...prev.validation_rules, maxSize: e.target.value }
                          }))}
                          placeholder="e.g., 5"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Date Field Configuration */}
                {fieldFormData.field_type === 'date' && (
                  <div className="bg-indigo-50 p-4 rounded-lg">
                    <h4 className="text-sm font-semibold text-gray-700 mb-4">Date Field Configuration</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Minimum Date</label>
                        <input
                          type="date"
                          value={fieldFormData.validation_rules?.minDate || ''}
                          onChange={(e) => setFieldFormData(prev => ({
                            ...prev,
                            validation_rules: { ...prev.validation_rules, minDate: e.target.value }
                          }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Maximum Date</label>
                        <input
                          type="date"
                          value={fieldFormData.validation_rules?.maxDate || ''}
                          onChange={(e) => setFieldFormData(prev => ({
                            ...prev,
                            validation_rules: { ...prev.validation_rules, maxDate: e.target.value }
                          }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Form Actions */}
                <div className="border-t pt-6 bg-gray-50 -mx-6 -mb-6 px-6 pb-6 rounded-b-lg">
                  <div className="flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={() => setShowFieldModal(false)}
                      className="px-6 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-6 py-2 bg-primary-600 text-white rounded-md text-sm font-medium hover:bg-primary-700 transition-colors"
                    >
                      {editingField ? 'Update' : 'Create'} Field
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default TemplateBuilder;